import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import type { ChangeEvent, JSX, SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { generatePcbPassword } from '@/api/Device'
import Card from '@/components/Card/Card'
import CardBody from '@/components/Card/CardBody'
import { useAuthContext } from '@/hooks/useAuthContext'
import { labelSx } from '@/styles/MuiComponentStyles'
import { layoutStyles } from '@/utils/theme'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

export const PcbPasswordGenerator = (): JSX.Element => {
  const { t } = useTranslation(['app', 'common'])
  const { enqueueSnackbar } = useSnackbar()
  const { isAuthenticated, isLoading: authLoading, profile } = useAuthContext()
  const [uid, setUid] = useState<string>('')
  const [password, setPassword] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // Only check privileges if user is authenticated and profile is loaded
  if (!isAuthenticated || !profile) {
    return <Navigate to="/v2/main/device/intake" replace />
  }

  // productionOnly is false when user has pm.service privilege
  const hasServicePrivilege = !profile.productionOnly

  // Redirect if user doesn't have the required privilege
  if (!hasServicePrivilege) {
    return <Navigate to="/v2/main/device/intake" replace />
  }

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()

    if (!uid.trim()) {
      setError(
        t('app:device.pcbPasswordGenerator.uidRequired') || 'UID is required',
      )
      return
    }

    setIsLoading(true)
    setError(null)
    setPassword(null)

    try {
      const response = await generatePcbPassword(uid.trim())
      setPassword(response.password)
      enqueueSnackbar(
        t('app:device.pcbPasswordGenerator.success') ||
          'Password generated successfully',
        {
          variant: 'success',
        },
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('app:device.pcbPasswordGenerator.error') ||
            'Failed to generate password'
      setError(errorMessage)
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUidChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUid(e.target.value)
    setError(null)
    setPassword(null)
  }

  const handleCopyPassword = async () => {
    if (!password) return

    try {
      await navigator.clipboard.writeText(password)
      enqueueSnackbar(t('common:general.copiedToClipboard'), {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar(t('common:general.copyFailed'), {
        variant: 'error',
      })
    }
  }

  return (
    <Box
      sx={{
        ...layoutStyles.pageContainer,
        mt: 0,
        p: 3,
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardBody>
              <Typography variant="subtitle1" sx={{ mb: 4 }}>
                {t('app:device.pcbPasswordGenerator.description') ||
                  'Enter any UID (main PCB, switch PCB, NMDI, etc.) to generate the matching password.'}
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      label={
                        t('app:device.pcbPasswordGenerator.uidLabel') || 'UID'
                      }
                      value={uid}
                      onChange={handleUidChange}
                      disabled={isLoading}
                      slotProps={{
                        inputLabel: { sx: labelSx },
                      }}
                      error={!!error}
                      helperText={error}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                      <Button
                        type="submit"
                        variant="outlined"
                        color="inherit"
                        fullWidth={false}
                        disabled={isLoading || !uid.trim()}
                        sx={{
                          backgroundColor: '#9c27b0',
                          color: '#fff',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: '#8e24aa',
                            boxShadow: 'none',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#9c27b0',
                            color: '#fff',
                            opacity: 0.65,
                          },
                        }}
                      >
                        {isLoading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            {t('common:loading') || 'Loading...'}
                          </>
                        ) : (
                          t('app:device.pcbPasswordGenerator.generateButton') ||
                          'Generate Password'
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {password && (
                <Alert severity="success" sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('app:device.pcbPasswordGenerator.passwordLabel') ||
                      'Generated Password'}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="code"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: 1,
                        borderRadius: 1,
                        wordBreak: 'break-all',
                        flexGrow: 1,
                      }}
                    >
                      {password}
                    </Typography>

                    <Tooltip title={t('common:general.copyToClipboard')}>
                      <IconButton
                        onClick={handleCopyPassword}
                        color="primary"
                        size="small"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Alert>
              )}
            </CardBody>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
